// Client for the /auth/* token-mediating backend, shared with tinuiti-frontend on the same host.
// The refresh token lives only in the HttpOnly `__Host-rt` cookie; access tokens live only in this
// module's memory. Until tinuiti-frontend's switch has shipped everywhere, a session can instead be
// a legacy readable `access_token` cookie, which this module only reads.

import Cookies from "js-cookie";

export class NotSignedInError extends Error {}

/** /auth/refresh gave no definitive answer (5xx, 403, network failure, malformed 200). The
 * session is not known to be gone: never sign out or redirect to login because of this. */
export class RefreshUnavailableError extends Error {}

/** Where this page's credentials come from. 'unavailable': the sign-in service didn't answer
 * and there is no legacy cookie; the caller shows the retry prompt, never the login redirect. */
export type SessionSource = "auth" | "legacy" | "none" | "unavailable";

interface Tokens {
  accessToken: string;
  /** Epoch milliseconds when the access token expires. */
  expiresAt: number;
}

// Refresh when less than this much life remains.
const REFRESH_BELOW_MS = 60 * 1000;
// Least life a cached token may have to be served when a refresh can't answer.
const STALE_MIN_MS = 10 * 1000;
// A refresh or logout request that hasn't answered by now counts as no answer.
const REQUEST_TIMEOUT_MS = 15 * 1000;
const RETURN_TO_MAX_LENGTH = 1024;

const LEGACY_AUTH_COOKIE_NAMES = [
  "access_token",
  "access_token_expiration",
  "google_api_access_token",
  "google_api_access_token_expiration",
  "login_method",
  "google_refresh_token",
];
// Browser storage keys left behind by the Amplify sessions bpm-frontend used to create.
const LEGACY_STORAGE_KEY_PREFIXES = ["CognitoIdentityServiceProvider.", "amplify-"];

// The configured cookie domain applies only when this host is that domain or a subdomain of it.
// Otherwise (local dev, preview URLs) cookies are scoped to the current host: a mismatched
// `domain` attribute makes the browser drop the cookie.
export const getAuthCookieDomain = (): string | null => {
  const configured = process.env.REACT_APP_DOMAIN;
  if (!configured) {
    return null;
  }
  const target = configured.startsWith(".") ? configured.slice(1) : configured;
  const host = window.location.hostname;
  return host === target || host.endsWith(`.${target}`) ? configured : null;
};

// Cookies may have been written under different domain scopes (e.g. .tinuiti.com vs
// .blisspoint.tinuiti.com), so removal has to cover every variant.
const cookieDomainScopes = (): string[] => {
  const { hostname } = window.location;
  const parts = hostname.split(".");
  const scopes = [getAuthCookieDomain(), hostname, `.${hostname}`];
  if (parts.length >= 3) {
    const parent = parts.slice(-2).join(".");
    scopes.push(parent, `.${parent}`);
  }
  return scopes.filter((d, i): d is string => d !== null && scopes.indexOf(d) === i);
};

// The in-memory session. Never persisted.
let tokens: Tokens | null = null;
// null until resolveSession() has run; see currentMode().
let mode: SessionSource | null = null;
// Bumped whenever the session ends. A refresh that started under an older generation must not
// write its result back, and a cached token from before the bump must not be served.
let generation = 0;

type RefreshResult =
  | { kind: "ok"; tokens: Tokens }
  | { kind: "unauthorized" }
  // This host has no /auth: 404, 405, another client error, or a 200 that isn't the token body.
  | { kind: "absent" }
  | { kind: "unavailable" }
  // The session ended while the request was in flight; its answer is discarded.
  | { kind: "stale" };

let refreshInFlight: Promise<RefreshResult> | null = null;

const hasLegacyCookies = (): boolean =>
  Boolean(Cookies.get("access_token")) && Boolean(Cookies.get("login_method"));

// A call made before resolveSession() has run (nothing normally does) uses whatever legacy
// cookie is present.
const currentMode = (): SessionSource => mode ?? (hasLegacyCookies() ? "legacy" : "none");

const remainingMs = (t: Tokens): number => t.expiresAt - Date.now();

const endSession = (): void => {
  tokens = null;
  generation += 1;
  refreshInFlight = null;
  mode = "none";
};

const postAuth = (path: string): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "X-Auth-Client": "1",
      "Content-Type": "application/json",
    },
    body: "{}",
    signal: controller.signal,
  }).finally(() => clearTimeout(timer));
};

const field = (o: object, key: string): unknown => Reflect.get(o, key);

// Epoch seconds on the wire, milliseconds in memory.
const parseTokens = (json: unknown): Tokens | null => {
  if (typeof json !== "object" || json === null) {
    return null;
  }
  const accessToken = field(json, "access_token");
  const expiresAt = field(json, "expires_at");
  if (
    typeof accessToken !== "string" ||
    accessToken.length === 0 ||
    typeof expiresAt !== "number" ||
    !Number.isFinite(expiresAt)
  ) {
    return null;
  }
  return { accessToken, expiresAt: expiresAt * 1000 };
};

// Never throws. Transport failures and retryable server conditions (403, 408, 429, 5xx) are
// 'unavailable'; any other non-401 answer means this host has no /auth.
const requestRefresh = async (): Promise<RefreshResult> => {
  let res: Response;
  try {
    res = await postAuth("/auth/refresh");
  } catch {
    return { kind: "unavailable" };
  }
  if (res.status === 200) {
    let parsed: Tokens | null = null;
    try {
      parsed = parseTokens(await res.json());
    } catch {
      parsed = null;
    }
    return parsed ? { kind: "ok", tokens: parsed } : { kind: "absent" };
  }
  if (res.status === 401) {
    return { kind: "unauthorized" };
  }
  if (res.status === 403 || res.status === 408 || res.status === 429 || res.status >= 500) {
    return { kind: "unavailable" };
  }
  return { kind: "absent" };
};

// A 200 moves the page onto the /auth session whatever mode it was in. An answer that arrives
// after the session ended is discarded.
const runRefresh = async (startedIn: number): Promise<RefreshResult> => {
  const result = await requestRefresh();
  if (startedIn !== generation) {
    return { kind: "stale" };
  }
  if (result.kind === "ok") {
    ({ tokens } = result);
    mode = "auth";
  }
  return result;
};

// Single-flight: concurrent callers share one POST /auth/refresh.
const joinRefresh = (): Promise<RefreshResult> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  const flight = runRefresh(generation);
  refreshInFlight = flight;
  // Registered before any caller awaits the flight, so it runs first. Only clears its own flight:
  // ending the session mid-flight already nulled it.
  const clear = () => {
    if (refreshInFlight === flight) {
      refreshInFlight = null;
    }
  };
  flight.then(clear, clear);
  return flight;
};

/**
 * Boot check, called once. POST /auth/refresh (same-origin credentials, `X-Auth-Client: 1`,
 * `Content-Type: application/json`, body `{}`). 200 with a valid body → 'auth' (tokens kept in
 * memory only). Otherwise, with the `access_token` and `login_method` cookies present → 'legacy'.
 * Without them: 401, 404, 405 or a non-JSON/malformed 200 (no `/auth` on this host) → 'none';
 * 5xx, 403 or a network error → 'unavailable'. Never throws.
 */
export const resolveSession = async (): Promise<SessionSource> => {
  let result: RefreshResult;
  try {
    result = await joinRefresh();
  } catch {
    result = { kind: "unavailable" };
  }
  if (result.kind === "ok") {
    return "auth";
  }
  let resolved: SessionSource = "none";
  if (hasLegacyCookies()) {
    resolved = "legacy";
  } else if (result.kind === "unavailable") {
    resolved = "unavailable";
  }
  mode = resolved;
  return resolved;
};

const readLegacyToken = (): string => {
  const token = Cookies.get("access_token");
  if (!token || !Cookies.get("login_method")) {
    throw new NotSignedInError("Not signed in");
  }
  return token;
};

const getLegacyAccessToken = async (forceRefresh: boolean): Promise<string> => {
  if (forceRefresh) {
    const result = await joinRefresh();
    if (result.kind === "ok") {
      return result.tokens.accessToken;
    }
    // A forced refresh means the cookie token was just rejected. With no /auth session to renew it
    // (401) or no /auth on this host, handing the same token back can't help. An outage says nothing
    // about the token, so that case falls through to the cookie.
    if (result.kind === "unauthorized" || result.kind === "absent") {
      throw new NotSignedInError("Not signed in");
    }
  }
  return readLegacyToken();
};

const getSessionAccessToken = async (
  forceRefresh: boolean,
  current: SessionSource
): Promise<string> => {
  if (!forceRefresh && tokens && remainingMs(tokens) >= REFRESH_BELOW_MS) {
    return tokens.accessToken;
  }
  const startedIn = generation;
  const result = await joinRefresh();
  if (result.kind === "ok") {
    return result.tokens.accessToken;
  }
  if (result.kind === "stale") {
    throw new NotSignedInError("Signed out during refresh");
  }
  if (result.kind === "unauthorized" || (result.kind === "absent" && current === "unavailable")) {
    endSession();
    throw new NotSignedInError("Not signed in");
  }
  // The refresh couldn't answer. A cached token is served only when this call didn't ask for a
  // fresh one, the session is the one the token came from, and it still has some life.
  if (!forceRefresh && tokens && generation === startedIn && remainingMs(tokens) >= STALE_MIN_MS) {
    return tokens.accessToken;
  }
  throw new RefreshUnavailableError("The sign-in service is temporarily unavailable.");
};

/**
 * The bearer token for API calls. In 'auth' mode: memory, refreshed single-flight when under 60 s
 * remain or on forceRefresh; on a refresh with no answer, the cached token if it has at least 10 s
 * left, else RefreshUnavailableError; on 401, NotSignedInError. In 'legacy' mode: the cookie
 * (never written or refreshed here); forceRefresh first tries /auth/refresh and switches to 'auth'
 * on 200. In 'none': NotSignedInError.
 */
export const getAccessToken = (opts?: { forceRefresh?: boolean }): Promise<string> => {
  const forceRefresh = opts?.forceRefresh ?? false;
  const current = currentMode();
  if (current === "none") {
    return Promise.reject(new NotSignedInError("Not signed in"));
  }
  if (current === "legacy") {
    return getLegacyAccessToken(forceRefresh);
  }
  return getSessionAccessToken(forceRefresh, current);
};

// Same rules as the sign-in service's return_to: a rooted path that can't be read as a
// protocol-relative URL, plain printable ASCII, no backslash.
const isSafeReturnTo = (candidate: string): boolean =>
  candidate.length <= RETURN_TO_MAX_LENGTH &&
  /^\/(?!\/)[\x21-\x7e]*$/.test(candidate) &&
  !candidate.includes("\\");

const loginBase = (): string => (process.env.REACT_APP_LOGIN_URL ?? "").replace(/\/+$/, "");

const loginTarget = (): string => {
  const base = loginBase();
  const { pathname, search } = window.location;
  const path = `${pathname}${search}`;
  if (pathname !== "/" && isSafeReturnTo(path)) {
    return `${base}/?return_to=${encodeURIComponent(path)}`;
  }
  return `${base}/`;
};

/**
 * Navigates (replace) to `${REACT_APP_LOGIN_URL ?? ''}/?return_to=<encoded path+search>` when the
 * current path is safe (starts with '/', no leading '//', no backslash, printable ASCII, at most
 * 1024 chars) and not '/', else to `${REACT_APP_LOGIN_URL}/`. Returns false without navigating when
 * that target is this page's own '/', so a host where bpm-frontend serves '/' can't loop.
 */
export const redirectToLogin = (): boolean => {
  const target = loginTarget();
  try {
    const url = new URL(target, window.location.origin);
    if (url.origin === window.location.origin && url.pathname === window.location.pathname) {
      return false;
    }
  } catch {
    return false;
  }
  window.location.replace(target);
  return true;
};

const removeLegacyCookies = (): void => {
  const scopes = cookieDomainScopes();
  LEGACY_AUTH_COOKIE_NAMES.forEach(name => {
    scopes.forEach(d => Cookies.remove(name, { domain: d }));
    // Also without a domain attribute, for cookies scoped to the exact origin.
    Cookies.remove(name);
  });
};

const removeLegacyStorage = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && LEGACY_STORAGE_KEY_PREFIXES.some(prefix => key.startsWith(prefix))) {
        keys.push(key);
      }
    }
    keys.forEach(key => window.localStorage.removeItem(key));
  } catch {
    // Storage blocked or unavailable: nothing to remove.
  }
};

// The backend only ever returns the Cognito hosted-UI logout endpoint on its prefix domain
// (<prefix>.auth.<region>.amazoncognito.com). Anything else is ignored rather than navigated to.
const COGNITO_LOGOUT_HOST_RE = /^[a-z0-9-]+\.auth\.[a-z0-9-]+\.amazoncognito\.com$/;

const parseLogoutUrl = (json: unknown): string | null => {
  if (typeof json !== "object" || json === null) {
    return null;
  }
  const value = field(json, "logout_url");
  if (typeof value !== "string" || value.length === 0) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const isCognitoLogout =
    url.protocol === "https:" &&
    url.port === "" &&
    COGNITO_LOGOUT_HOST_RE.test(url.hostname) &&
    url.pathname === "/logout";
  return isCognitoLogout ? url.href : null;
};

/**
 * POST /auth/logout (best effort: a failure or no answer still continues), clear memory, remove
 * the legacy auth cookies (including `google_refresh_token`) across the same domain scopes
 * `clearAuthCookies` covers, remove localStorage keys starting `CognitoIdentityServiceProvider.`
 * or `amplify-`, then navigate to `logout_url` when the response has one, else to the login URL.
 */
export const signOut = async (): Promise<void> => {
  let logoutUrl: string | null = null;
  try {
    const res = await postAuth("/auth/logout");
    logoutUrl = parseLogoutUrl(await res.json());
  } catch (e) {
    console.error("auth-client: /auth/logout did not complete", e);
  }
  endSession();
  removeLegacyCookies();
  removeLegacyStorage();
  window.location.replace(logoutUrl ?? `${loginBase()}/`);
};
